import apiClient from "../lib/api-client";
import { Supplier } from "./supplier.service";

export type GRNStatus =
  | "DRAFT"
  | "COMPLETED"
  | "CANCELLED"
  | "PARTIALLY_RETURNED"
  | "FULLY_RETURNED";

export interface GoodsReceiptItem {
  id: string;
  purchaseOrderItemId?: string;
  productId: string;
  product: {
    id: string;
    name: string;
    sku: string;
    barcode?: string;
    unit: string;
    manufacturer?: string;
  };
  batchId?: string;
  batch?: {
    id: string;
    batchNumber: string;
    quantity: number;
  };
  batchNumber: string;
  mfgDate?: string;
  expiryDate: string;
  quantity: number;
  freeQuantity: number;
  receivedQty: number;
  purchasePackId?: string;
  unitsPerPack?: number;
  baseQuantityReceived?: number;
  unitPrice: number;
  mrp: number;
  sellingPrice: number;
  discountPercent: number;
  discountAmount: number;
  schemeDiscountPercent?: number;
  schemeDiscountAmount?: number;
  taxPercent: number;
  taxAmount: number;
  totalAmount: number;
  rackLocation?: string;
  returnedQuantity?: number;
  returnableQuantity?: number;
  returnableBuckets?: {
    available: number;
    quarantine: number;
    damaged: number;
  };
}

export interface ShelfLifeWarning {
  productId: string;
  productName: string;
  batchNumber: string;
  expiryDate: string;
  remainingDays: number;
  thresholdDays: number;
}

export interface GoodsReceipt {
  id: string;
  tenantId: string;
  storeId: string;
  supplierId: string;
  supplier: Supplier | { id: string; name: string; phone?: string };
  purchaseOrderId?: string;
  purchaseOrder?: {
    id: string;
    poNumber: string;
    poDate: string;
    status: string;
  };
  purchaseInvoices?: Array<{
    id: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate?: string;
    totalPaise: string;
    status: "DRAFT" | "POSTED" | "VOIDED";
  }>;
  receivedById: string;
  grnNumber: string;
  grnDate: string;
  supplierInvoiceNo?: string;
  supplierInvoiceDate?: string;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  freightAmount: number;
  handlingAmount: number;
  otherCharges: number;
  totalAmount: number;
  paidAmount: number;
  dueDate?: string;
  status: GRNStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  items: GoodsReceiptItem[];
  shelfLifeWarnings?: ShelfLifeWarning[];
  _count?: {
    items: number;
  };
}

export interface CreateGoodsReceiptItemDto {
  purchaseOrderItemId?: string;
  productId: string;
  batchNumber: string;
  mfgDate?: string;
  expiryDate: string;
  quantity: number;
  freeQuantity?: number;
  receivedQty?: number;
  purchasePackId?: string;
  unitsPerPack?: number;
  unitPrice: number | string;
  mrp: number | string;
  sellingPrice: number | string;
  discountPercent?: number;
  discountAmount?: number;
  schemeDiscountPercent?: number;
  taxPercent?: number;
  taxAmount?: number;
  totalAmount?: number;
  rackLocation?: string;
  // UI-only fields (not sent to API)
  _productName?: string;
  _productSku?: string;
  _productUnit?: string;
  _productManufacturer?: string;
  _packDescription?: string;
  _baseQuantity?: number;
  _poFreeQuantity?: number;
}

export interface CreateGoodsReceiptDto {
  supplierId: string;
  storeId?: string;
  purchaseOrderId?: string;
  grnDate?: string;
  supplierInvoiceNo?: string;
  supplierInvoiceDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  items: CreateGoodsReceiptItemDto[];
  discountAmount?: number;
  freightAmount?: number;
  handlingAmount?: number;
  otherCharges?: number;
  totalAmount?: number;
  notes?: string;
}

function mutationKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export const goodsReceiptService = {
  async getGoodsReceipts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: GRNStatus;
    supplierId?: string;
    storeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: GoodsReceipt[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get("/goods-receipts", { params });
    return response.data;
  },

  async getGoodsReceipt(id: string): Promise<GoodsReceipt> {
    const response = await apiClient.get(`/goods-receipts/${id}`);
    return response.data;
  },

  async createGoodsReceipt(data: CreateGoodsReceiptDto): Promise<GoodsReceipt> {
    const storageKey = `medicorepay:goods-receipt:create:${data.storeId || "selected"}:${data.supplierId}`;
    const response = await apiClient.post("/goods-receipts", data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async completeGoodsReceipt(id: string): Promise<GoodsReceipt> {
    const storageKey = `medicorepay:goods-receipt:complete:${id}`;
    const response = await apiClient.patch(
      `/goods-receipts/${id}/complete`,
      undefined,
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async cancelGoodsReceipt(id: string): Promise<GoodsReceipt> {
    const storageKey = `medicorepay:goods-receipt:cancel:${id}`;
    const response = await apiClient.patch(
      `/goods-receipts/${id}/cancel`,
      undefined,
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },
};

export const GRN_STATUS_LABELS: Record<GRNStatus, string> = {
  DRAFT: "Draft",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  PARTIALLY_RETURNED: "Partially Returned",
  FULLY_RETURNED: "Fully Returned",
};

export const GRN_STATUS_COLORS: Record<GRNStatus, string> = {
  DRAFT: "bg-yellow-100 text-yellow-800",
  COMPLETED: "bg-green-100 text-green-800",
  CANCELLED: "bg-red-100 text-red-800",
  PARTIALLY_RETURNED: "bg-orange-100 text-orange-800",
  FULLY_RETURNED: "bg-purple-100 text-purple-800",
};
